import { backendUrl as defaultBackend } from "../config_prod";

const VISITOR_ID_KEY = "kscan_ats_visitor_id";
const SESSION_ID_KEY = "kscan_ats_session_id";
const SESSION_LAST_ACTIVITY_KEY = "kscan_ats_session_last_activity";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

export type ATSAnalyticsEventName =
  | "job_view"
  | "form_started"
  | "cv_uploaded"
  | "application_submitted"
  | "application_failed";

export type ATSAttribution = {
  source: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
};

function uuid(): string {
  if (window.crypto && "randomUUID" in window.crypto) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function storageGet(key: string): string | null {
  try {
    const value = window.localStorage.getItem(key);
    if (value) return value;
  } catch {
    // fall back to cookie below
  }
  const cookie = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${key}=`));
  return cookie
    ? decodeURIComponent(cookie.split("=").slice(1).join("="))
    : null;
}

function storageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
  }
}

export function getVisitorId(): string {
  const existing = storageGet(VISITOR_ID_KEY);
  if (existing) return existing;
  const next = uuid();
  storageSet(VISITOR_ID_KEY, next);
  return next;
}

export function getSessionId(): string {
  const now = Date.now();
  const existing = storageGet(SESSION_ID_KEY);
  const lastActivity = Number(storageGet(SESSION_LAST_ACTIVITY_KEY) || "0");
  if (existing && lastActivity && now - lastActivity < SESSION_TIMEOUT_MS) {
    storageSet(SESSION_LAST_ACTIVITY_KEY, String(now));
    return existing;
  }

  const next = uuid();
  storageSet(SESSION_ID_KEY, next);
  storageSet(SESSION_LAST_ACTIVITY_KEY, String(now));
  return next;
}

export function readAttributionParams(): ATSAttribution {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source") || "";
  return {
    source: params.get("source") || "",
    utmSource,
    utmMedium: params.get("utm_medium") || "",
    utmCampaign: params.get("utm_campaign") || "",
  };
}

function getDeviceType(): string {
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
    return "mobile";
  return "desktop";
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "edge";
  if (/Chrome\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "safari";
  if (/Firefox\//.test(ua)) return "firefox";
  return "unknown";
}

export async function trackATSAnalyticsEvent({
  backendUrl = defaultBackend,
  event,
  jobId,
  attribution,
  metadata = {},
}: {
  backendUrl?: string;
  event: ATSAnalyticsEventName;
  jobId: string;
  attribution: ATSAttribution;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  if (!jobId) return;

  const payload = {
    event,
    timestamp: new Date().toISOString(),
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    job_id: jobId,
    source: attribution.source,
    utm_source: attribution.utmSource,
    utm_medium: attribution.utmMedium,
    utm_campaign: attribution.utmCampaign,
    device_type: getDeviceType(),
    browser: getBrowser(),
    country: "",
    language: navigator.language || "",
    referer: document.referrer || "",
    user_agent: navigator.userAgent || "",
    metadata,
  };

  try {
    const res = await fetch(`${backendUrl}/kscan/v1/ats/post/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!res.ok) {
      // Analytics should not block the application funnel.
      console.warn("ATS analytics event failed", res.status);
    }
  } catch (err) {
    console.warn("ATS analytics event failed", err);
  }
}
